import React, { useEffect, useState } from 'react';
import { SERVER_URL, flushWrites } from '../data/serverStore';

const apiFetch = (path, options = {}) => fetch(`${SERVER_URL}${path}`, {
  credentials: 'include',
  ...options,
});

const fmtDate = value => value ? new Date(value).toLocaleString() : 'Never';

export default function AutoBackupPanel({ toast }) {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');

  const refresh = async () => {
    try {
      const res = await apiFetch('/api/backups');
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Could not load backup status');
      setStatus(body);
      setError('');
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => { refresh(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      await flushWrites();
      const res = await apiFetch('/api/backups/run', {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({ reason:'manual run from auto-backup panel' }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Backup failed');
      toast('success', 'Complete local and offsite backup finished');
      await refresh();
    } catch (e) {
      setError(e.message);
      toast('error', e.message);
    } finally {
      setRunning(false);
    }
  };

  const latest = status?.latest;
  const retention = status?.retention || {};

  return (
    <div className="content">
      <div style={{maxWidth:850}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
          <div>
            <h2 style={{margin:0,color:'#1e3060'}}>Server Auto-Backup</h2>
            <div style={{fontSize:11,color:'#777',marginTop:3}}>
              Runs on the server even when no browser is open
            </div>
          </div>
          <button className="btn" onClick={refresh} disabled={running}>↻ Refresh</button>
        </div>

        {error && <div className="notice notice-error" style={{marginBottom:12}}>{error}</div>}

        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:9,marginBottom:14}}>
          {[
            ['Storage', status?.storage?.mode || '—', '#1a7dc4'],
            ['Latest backup', fmtDate(latest?.createdAt), latest ? '#1a6b42' : '#b83232'],
            ['Google Drive', status?.offsiteConfigured ? 'Connected' : 'Not configured',
              status?.offsiteConfigured ? '#1a6b42' : '#a06000'],
            ['Backup copies', status?.backups?.length ?? '—', '#1e3060'],
          ].map(([label,value,color]) => (
            <div key={label} style={{background:'white',border:'1px solid #e0e4ec',
              borderTop:`3px solid ${color}`,borderRadius:7,padding:'11px 13px'}}>
              <div style={{fontSize:13,fontWeight:750,color}}>{value}</div>
              <div style={{fontSize:9.5,color:'#888',textTransform:'uppercase',marginTop:3}}>{label}</div>
            </div>
          ))}
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
          <div className="panel">
            <div className="panel-header">Schedule</div>
            <div className="panel-body" style={{fontSize:12,lineHeight:1.9}}>
              <div>✅ Database snapshot every <strong>hour</strong></div>
              <div>✅ Complete database + files every <strong>day</strong></div>
              <div>✅ Long-term archive on the <strong>first day of each month</strong></div>
              <div>✅ Emergency backup automatically before every restore</div>
            </div>
          </div>

          <div className="panel">
            <div className="panel-header">Retention</div>
            <div className="panel-body" style={{fontSize:12,lineHeight:1.9}}>
              <div>Hourly database copies: <strong>{retention.hourly ?? 168}</strong></div>
              <div>Daily complete copies: <strong>{retention.daily ?? 30}</strong></div>
              <div>Monthly archives: <strong>{retention.monthly ?? 120}</strong> (10 years)</div>
              <div>Manual complete copies: <strong>{retention.manual ?? 10}</strong></div>
            </div>
          </div>
        </div>

        <button className="btn btn-primary" style={{marginTop:14,padding:'9px 22px',fontWeight:700}}
          onClick={runNow} disabled={running}>
          {running ? '⏳ Creating complete backup…' : '▶ Run Complete Backup Now'}
        </button>

        <div style={{marginTop:12,background:'#f0f4ff',border:'1px solid #d0d8f0',
          borderRadius:7,padding:'10px 13px',fontSize:11,lineHeight:1.6,color:'#555'}}>
          Browser-based backup storage has been retired. Backups now run independently
          on the server and include the database, COC images, results sheets, manifest
          and file checksums.
        </div>
      </div>
    </div>
  );
}
